import { useState, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

// 24小时（毫秒）
const SESSION_TTL_MS = 24 * 60 * 60 * 1000;
// localStorage key，记录前端最后一次登录成功的时间戳
const LOGIN_TIME_KEY = 'jinan_user_login_at';

export function markUserLoginTime() {
  try {
    localStorage.setItem(LOGIN_TIME_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

function isSessionExpiredLocally(): boolean {
  try {
    const raw = localStorage.getItem(LOGIN_TIME_KEY);
    if (!raw) return false; // 无记录时不强制踢出，由 Supabase session 自己判断
    const loginAt = Number(raw);
    return Date.now() - loginAt > SESSION_TTL_MS;
  } catch {
    return false;
  }
}

export default function AuthGuard() {
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    let mounted = true;

    // 先做本地 24 小时检查
    if (isSessionExpiredLocally()) {
      supabase.auth.signOut().finally(() => {
        try { localStorage.removeItem(LOGIN_TIME_KEY); } catch { /* ignore */ }
        if (mounted) {
          setAuthed(false);
          setLoading(false);
        }
      });
      return () => { mounted = false; };
    }

    // 获取当前 session（带简单重试）
    const tryGetSession = async (attempts: number) => {
      for (let i = 0; i < attempts; i++) {
        try {
          const { data } = await supabase.auth.getSession();
          if (!mounted) return;
          const hasSession = !!data.session;
          setAuthed(hasSession);
          setLoading(false);
          return;
        } catch {
          if (i < attempts - 1) {
            await new Promise((r) => setTimeout(r, 1000 * 2 ** i));
          }
        }
      }
      if (mounted) {
        setAuthed(false);
        setLoading(false);
      }
    };

    tryGetSession(3);

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setAuthed(!!session);
      setLoading(false);
    });

    // 每分钟检查一次本地 24 小时登录时间戳，过期则强制退出（与后台 AdminGuard 保持一致）
    const timer = setInterval(() => {
      if (isSessionExpiredLocally()) {
        supabase.auth.signOut().finally(() => {
          try { localStorage.removeItem(LOGIN_TIME_KEY); } catch { /* ignore */ }
          setAuthed(false);
        });
      }
    }, 60 * 1000);

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
      clearInterval(timer);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brown-50">
        <span className="w-8 h-8 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin"></span>
      </div>
    );
  }

  if (!authed) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}